import TextBox from "../../../components/form/TextBox";
import Icon from "../../../components/base/Icon";


export type AddonChoice = "included" | "paid" | "off"

// Everything a custom plan is, as the page holds it: text while it is being typed.
export interface CustomPlanForm {
    price: string
    users: string
    messages: string
    minutes: string
    numbers: string
    messageRate: string
    minuteRate: string
    numberPrice: string
    whatsApp: AddonChoice
    telegram: AddonChoice
    maskedJobCalls: AddonChoice
}

export interface AddonPrices {
    whatsApp: number
    telegram: number
    maskedJobCalls: number
}

export type AddonKey = "whatsApp" | "telegram" | "maskedJobCalls"

export const ADDONS: { key: AddonKey, label: string }[] = [
    { key: "whatsApp", label: "WhatsApp" },
    { key: "telegram", label: "Telegram" },
    { key: "maskedJobCalls", label: "Masked Job Calls" }
]

export const parseNumber = (v: string) => Number.parseFloat((v ?? "").replace(/,/g, ""))

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })


/** What the custom plan costs a month from the renewal: its price and every add-on sold on it. */
export function customMonthly(form: CustomPlanForm, prices?: AddonPrices) {
    const price = parseNumber(form.price)
    const addons = ADDONS.reduce((s, a) => s + (form[a.key] === "paid" ? (prices?.[a.key] ?? 0) : 0), 0)
    return (Number.isNaN(price) ? 0 : price) + addons
}

/** The form as the hub takes it. */
export function customTerms(form: CustomPlanForm) {
    const n = (v: string) => { const x = parseNumber(v); return Number.isNaN(x) ? -1 : x }
    return {
        price: n(form.price),
        users: n(form.users),
        messages: n(form.messages),
        minutes: n(form.minutes),
        numbers: n(form.numbers),
        messageRate: n(form.messageRate),
        minuteRate: n(form.minuteRate),
        numberPrice: n(form.numberPrice),
        whatsApp: form.whatsApp,
        telegram: form.telegram,
        maskedJobCalls: form.maskedJobCalls
    }
}


// One add-on's choice: a row of segments. Leaving out "included" offers only paid or off -- an
// add-on a catalog plan does not include.
export function AddonChoiceRow(props: {
    label: string
    price?: number
    value: AddonChoice
    choices?: AddonChoice[]
    onChange: (v: AddonChoice) => void
}) {
    const all: [AddonChoice, string][] = [
        ["included", "Included"],
        ["paid", `Paid · ${money.format(props.price ?? 0)}/mo`],
        ["off", "Off"]
    ]
    const choices = props.choices ?? ["included", "paid", "off"]

    return (
        <div className="migration-custom-addon">
            <div className="fw-semibold">{props.label}</div>
            <div className="migration-segments">
                {
                    all.filter(([key]) => choices.includes(key)).map(([key, text]) =>
                        <button key={key} type="button"
                            className={"migration-segment" + (props.value === key ? " is-active" : "")}
                            onClick={e => { e.preventDefault(); props.onChange(key) }}>
                            {text}
                        </button>
                    )
                }
            </div>
        </div>
    )
}


// A plan made for one customer: every figure set here, and for each add-on whether the plan
// includes it, sells it to them at its price from the renewal, or leaves it off.
export default function CustomPlanFields(props: {
    form: CustomPlanForm
    prices?: AddonPrices
    onChange: (form: CustomPlanForm) => void
    // The customer's Plan tab reuses these fields: a yearly plan's price is a year's, and the
    // note below them is its own.
    priceLabel?: string
    note?: string
}) {

    const f = props.form
    const set = (key: keyof CustomPlanForm, value: any) => props.onChange({ ...f, [key]: value })

    const field = (key: keyof CustomPlanForm, label: string, kind: "money" | "integer" | "rate", hint?: string) =>
        <div className="migration-custom-field">
            <TextBox id={"custom-" + key} name={"custom" + key.charAt(0).toUpperCase() + key.slice(1)}
                label={label} labelUp block noMarginBottom
                money={kind === "money"} numeric={kind === "integer"} decimal={kind === "rate"}
                iconText={kind === "integer" ? undefined : "$"} iconStart={kind !== "integer"}
                value={f[key] as string} onTextChanges={v => set(key, v)} />
            {hint && <div className="migration-custom-hint">{hint}</div>}
        </div>

    return (
        <div className="migration-custom">

            <div className="migration-custom-grid">
                {field("price", props.priceLabel ?? "Monthly price", "money", "0 for a free account")}
                {field("users", "Users at once", "integer")}
            </div>

            <div className="migration-custom-title">Included every month</div>
            <div className="migration-custom-grid is-three">
                {field("messages", "Messages", "integer")}
                {field("minutes", "Call minutes", "integer")}
                {field("numbers", "Phone numbers", "integer")}
            </div>

            <div className="migration-custom-title">Beyond that, from the wallet</div>
            <div className="migration-custom-grid is-three">
                {field("messageRate", "Each message", "rate")}
                {field("minuteRate", "Each minute", "rate")}
                {field("numberPrice", "Each number / month", "rate")}
            </div>

            <div className="migration-custom-title">Add-ons</div>
            <div className="migration-custom-addons">
                {
                    ADDONS.map(a =>
                        <AddonChoiceRow key={a.key} label={a.label} price={props.prices?.[a.key]}
                            value={f[a.key]} onChange={v => set(a.key, v)} />
                    )
                }
            </div>

            <div className="migration-custom-hint mt-3">
                <Icon icon="circle-info" type="solid" size={10} className="me-1" />
                {props.note ?? "Call recording is on for every account. Extra users are not sold on a custom plan — set the number above."}
            </div>
        </div>
    )
}
